/**
 * Client-side 90°-clockwise rotation of an image blob, using a 2D canvas.
 *
 * Used by the manual-rotate session-card action: when scan-correct gets
 * the orientation wrong (rare but real on receipts photographed at a
 * shallow angle), the user clicks ⟲ on the session card and we rotate
 * the corrected JPEG, then re-feed it through the regular pipeline so
 * OCR runs on the right orientation.
 *
 * Output is always JPEG — the input we're called with is the
 * post-scan-correct JPEG, which is what the user is actually looking
 * at, so a consistent JPEG round-trip keeps quality predictable.
 */
export async function rotateBlob90Clockwise(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    // Width and height swap because we're rotating ±90°.
    canvas.width = bitmap.height;
    canvas.height = bitmap.width;
    const ctx = canvas.getContext('2d');
    if (ctx === null) {
      throw new Error('canvas 2d context unavailable');
    }
    // Translate to the post-rotation top-left, then rotate so the
    // source's (0, 0) lands where we want it on the rotated canvas.
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(bitmap, 0, 0);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) =>
          b !== null
            ? resolve(b)
            : reject(new Error('canvas.toBlob returned null')),
        'image/jpeg',
        0.92,
      );
    });
  } finally {
    bitmap.close();
  }
}
